import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { Cpu, FileText, Settings, Download } from "lucide-react";

const DevelopmentModels = () => {
  const features = [
    {
      icon: <Cpu className="h-8 w-8 text-blue-600" />,
      title: "Sistema CAD-CAM",
      description: "Moderno laboratorio con sistema CAD-CAM per lo sviluppo preciso dei modelli"
    },
    {
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      title: "Graduazione Taglie",
      description: "Sviluppo completo in taglie dei modelli in cartone fibrato"
    },
    {
      icon: <Settings className="h-8 w-8 text-blue-600" />,
      title: "Correzioni Personalizzate",
      description: "Soddisfiamo ogni necessità di correttivo o variazione locale"
    },
    {
      icon: <Download className="h-8 w-8 text-blue-600" />,
      title: "Lavorazione Remota",
      description: "Graduazione anche a distanza tramite file DXF o compatibili Comelz P4CN"
    }
  ];

  const compatibleFormats = [
    "File DXF",
    "File compatibili Punzonatrice Comelz P4CN",
    "Programma di controllo Caligola",
    "Formati CAD standard"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Cpu className="h-16 w-16 mx-auto mb-6 text-green-200" />
            <h1 className="text-5xl font-bold mb-6">SVILUPPO MODELLI</h1>
            <p className="text-xl text-green-100 leading-relaxed">
              Moderno laboratorio con sistema CAD-CAM per lo sviluppo 
              in taglie dei modelli in cartone fibrato o policarbonato compatto
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Tecnologia Avanzata
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  La nostra azienda da alcuni anni dispone al suo interno di un moderno 
                  laboratorio con sistema CAD-CAM per lo sviluppo in taglie dei modelli 
                  in cartone fibrato o policarbonato compatto, soddisfando ogni possibile necessità di correttivo 
                  o variazione locale.
                </p>
                <p>
                  La graduazione dei modelli può essere effettuata anche a distanza 
                  grazie all'invio di file in DXF o file compatibile con la punzonatrice 
                  Comelz P4CN e con il suo programma di controllo "Caligola".
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/7qubckzz_Photoroom_20251018_085643.PNG"
                alt="Punzonatrice P4 COMELZ in azione"
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center h-full flex flex-col">
                  <div className="mb-6 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed flex-grow">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Process Section */}
          <div className="bg-gray-50 rounded-2xl p-12 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Processo di Sviluppo
              </h2>
              <p className="text-lg text-gray-600">
                Come lavoriamo per sviluppare i vostri modelli
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Ricezione File</h3>
                <p className="text-gray-600">Acquisizione del vostro disegno o file digitale</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Elaborazione CAD</h3>
                <p className="text-gray-600">Lavorazione con sistema CAD-CAM avanzato</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Graduazione</h3>
                <p className="text-gray-600">Sviluppo completo in tutte le taglie richieste</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">4</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Consegna</h3>
                <p className="text-gray-600">Modelli pronti in cartone fibrato</p>
              </div>
            </div>
          </div>

          {/* Compatible Formats */}
          <div className="text-center mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-12">
              Formati Compatibili
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {compatibleFormats.map((format, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
                  <FileText className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <p className="font-medium text-gray-800">{format}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              I Nostri Lavori
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_b6387205-1c39-4170-9826-13890af1b37d/artifacts/pdsfsp17_sviluppo%20modelli.png"
                    alt="Sviluppo modelli 1"
                    className="w-full h-[28rem] object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Modelli Calzature</h3>
                    <p className="text-gray-600">Sviluppo completo per calzaturifici</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_b6387205-1c39-4170-9826-13890af1b37d/artifacts/rbvvxq5f_sviluppo%20modelli%201.png"
                    alt="Sviluppo modelli 2"
                    className="w-full h-[28rem] object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Tavola Grafica</h3>
                    <p className="text-gray-600">Acquisizione del vostro disegno con precisione</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/zo3d71ga_Photoroom_20251015_181424.JPG"
                    alt="Punzonatrice con modelli Texan in taglio"
                    className="w-full h-[28rem] object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Tecnologia Comelz</h3>
                    <p className="text-gray-600">Punzonatrice P4CN con controllo Caligola</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevelopmentModels;